// mikoBrain.ts — client side of the AI brain.
//
// The Anthropic call CANNOT run in the browser: an API key must never ship in
// client code. So the browser POSTs the player's transcript + live stats to a
// same-origin dev endpoint (/api/miko, served by the Vite middleware in
// vite.config.ts), and that server-side handler holds the key and calls Claude
// (claude-sonnet-4-6). See vite.config.ts for the proxy + the exact system
// prompt.
//
// If the key isn't configured the endpoint still returns 200 with a friendly
// canned line, so the mic + text-to-speech keep working offline.

export type MikoStats = {
  cash: number; // B$ liquid
  empireValue: number; // B$ total wealth (net worth + cash)
  freedomPct: number; // 0..100+
  venture: string; // active venture, human label
};

export type MikoReply = { text: string; source: 'ai' | 'fallback' };

/** Ask Miko a question. Never throws — returns a fallback line on any failure so
 *  the voice UX degrades gracefully. */
export async function askMiko(prompt: string, stats: MikoStats): Promise<MikoReply> {
  try {
    const res = await fetch('/api/miko', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ prompt, stats }),
    });
    if (!res.ok) throw new Error(`miko ${res.status}`);
    const data = (await res.json()) as { text?: string; source?: 'ai' | 'fallback' };
    const text = (data.text ?? '').trim();
    if (!text) throw new Error('empty');
    return { text, source: data.source ?? 'ai' };
  } catch {
    return { text: localFallback(stats), source: 'fallback' };
  }
}

// A little on-brand hype when the brain is unreachable — keeps Miko in character
// so a missing key / offline state never feels broken to a kid.
function localFallback(stats: MikoStats): string {
  const lines = [
    `You're sitting on ${money(stats.cash)}. Set a price and drop it — go!`,
    `Freedom at ${Math.round(stats.freedomPct)}%. Run one more week, right now.`,
    `${stats.venture} is your lane today. Make a move and let's stack it.`,
    `Empire value ${money(stats.empireValue)} and climbing. Keep pushing — sell something today.`,
  ];
  // Deterministic-ish pick without Math.random dependence on any single field.
  const i = Math.abs(stats.cash + Math.round(stats.freedomPct) + stats.venture.length) % lines.length;
  return lines[i]!;
}

function money(n: number): string {
  return `B$${Math.round(n).toLocaleString()}`;
}
